/** The town's stable ten-key transport maps to full piece roles; no texture-key format changes. */
import type { TownPropPalette } from '../../scene/townPropTemplates';
import type { PaletteRole, ResolvedPalette } from './contract';
import { PIECE_PALETTES, piecePalette } from './palettes';
import { mix } from './svg';

const TOWN_ROLE: Readonly<Record<keyof TownPropPalette, PaletteRole>> = {
  primary: 'paperBase', shade: 'paperShadow', roof: 'clothBlue', roofEdge: 'clothBlueLight', wood: 'woodBase',
  woodEdge: 'woodLight', stone: 'stoneBase', iron: 'outline', glass: 'warmCore', glassCore: 'paperLight',
};
const keys = Object.keys(TOWN_ROLE) as Array<keyof TownPropPalette>;
const hex = (v: number) => '#' + v.toString(16).padStart(6, '0');

export function pieceTownPalette(roles: ResolvedPalette): TownPropPalette {
  return Object.fromEntries(keys.map(k => [k, parseInt(roles[TOWN_ROLE[k]].slice(1), 16)])) as unknown as TownPropPalette;
}

/** Exact registry matches carry the unmapped moss/reed/salt/water roles along with their town colours. */
export function piecePaletteRoles(id: string, p: TownPropPalette,
  registry: Readonly<Record<string, ResolvedPalette>> = PIECE_PALETTES): ResolvedPalette {
  const base = piecePalette(id, registry);
  if (!p || keys.some(k => !Number.isInteger(p[k]) || p[k] < 0 || p[k] > 0xffffff)) throw new Error('Invalid town piece palette');
  const candidates = [base, ...Object.entries(registry).filter(([key]) => key !== id).map(([, v]) => v)];
  for (const roles of candidates) if (keys.every(k => hex(p[k]) === roles[TOWN_ROLE[k]].toLowerCase())) return roles;
  const [stone, iron, wood, roof] = [hex(p.stone), hex(p.iron), hex(p.wood), hex(p.roof)];
  return {
    ...base,
    outline: iron, outlineSoft: mix(iron, stone, 0.5),
    paperBase: hex(p.primary), paperShadow: hex(p.shade), paperLight: hex(p.glassCore), warmCore: hex(p.glass),
    woodBase: wood, woodLight: hex(p.woodEdge), woodShadow: mix(wood, iron, 0.4),
    clothBlue: roof, clothBlueLight: hex(p.roofEdge), clothBlueShadow: mix(roof, iron, 0.35),
    stoneBase: stone, stoneLight: mix(stone, hex(p.glassCore), 0.35), stoneShadow: mix(stone, iron, 0.35),
  };
}
